import { cloneDeep } from 'lodash';
import { useRef } from 'react';
import { getFramework, trackConfig, useIdentity } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { convertLayerPointToOrigin, getCenterPointForOrigin } from '@/utils/layer-kit';
import { addPoint, rotatePointWithFocus, subPoint } from '@/utils/math-kit';
import { ILayerProps, IMarkupLayerProps, IPoint, LayerType, ResizeOperateType } from '@/version-types';
import { computedSegmentBaseInfo, updateScreenshotUrl } from '../utils';

export const useSegmentOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const leftStartPoint = useRef<IPoint>();
    const rightStartPoint = useRef<IPoint>();
    const centerOffsetPoint = useRef<IPoint>();
    const originCenterPoint = useRef<IPoint>();
    const mouseStartPoint = useRef<IPoint>();
    const leftTopPoint = useRef<IPoint>();
    const framework = getFramework(identity);

    return {
        /** 控制2边端点 */
        point(e: ITouchKitEvent, layer: ILayerProps, operateType: ResizeOperateType) {
            const scale = framework.stage.getScale();
            if (e.isStart) {
                framework.operate.setIsBaseOperate(true);
                const data = layer.data as IMarkupLayerProps;
                oldInfo.current = cloneDeep(layer);
                const centerPoint = getCenterPointForOrigin(layer);
                originCenterPoint.current = centerPoint;
                leftTopPoint.current = {
                    x: layer.left,
                    y: layer.top,
                };
                rightStartPoint.current = rotatePointWithFocus(
                    addPoint(addPoint(data.anchor.points![1], data.centerOffset), leftTopPoint.current),
                    centerPoint,
                    layer.rotate,
                );
                leftStartPoint.current = rotatePointWithFocus(
                    addPoint(addPoint(data.anchor.points![0], data.centerOffset), leftTopPoint.current),
                    centerPoint,
                    layer.rotate,
                );
                centerOffsetPoint.current = rotatePointWithFocus(
                    addPoint(data.centerOffset, leftTopPoint.current),
                    centerPoint,
                    layer.rotate,
                );
                mouseStartPoint.current = convertLayerPointToOrigin(e.currentPoint, scale);
            }

            if (
                !oldInfo.current ||
                !mouseStartPoint.current ||
                !originCenterPoint.current ||
                !rightStartPoint.current ||
                !leftStartPoint.current ||
                !centerOffsetPoint.current ||
                !leftTopPoint.current
            )
                return;

            const mouseCurPoint = convertLayerPointToOrigin(e.currentPoint, scale);

            const offsetPoint = {
                x: ~~(mouseCurPoint.x - mouseStartPoint.current.x),
                y: ~~(mouseCurPoint.y - mouseStartPoint.current.y),
            };

            const newLayer = cloneDeep(oldInfo.current);
            const data = newLayer.data as IMarkupLayerProps;

            if (data.imageTransform.flipH) {
                offsetPoint.x *= -1;
            }

            if (data.imageTransform.flipV) {
                offsetPoint.y *= -1;
            }

            const stageCenterOffset = addPoint(data.centerOffset, leftTopPoint.current);
            let centerPointOffset: any;
            if (operateType === ResizeOperateType.RC) {
                data.anchor.points![1] = subPoint(
                    rotatePointWithFocus(
                        addPoint(offsetPoint, rightStartPoint.current),
                        originCenterPoint.current,
                        -layer.rotate,
                    ),
                    stageCenterOffset,
                );
            } else if (operateType === ResizeOperateType.LC) {
                data.anchor.points![0] = subPoint(
                    rotatePointWithFocus(
                        addPoint(offsetPoint, leftStartPoint.current),
                        originCenterPoint.current,
                        -layer.rotate,
                    ),
                    stageCenterOffset,
                );
            } else {
                const newCenterOffset = subPoint(
                    rotatePointWithFocus(
                        addPoint(offsetPoint, centerOffsetPoint.current),
                        originCenterPoint.current,
                        -layer.rotate,
                    ),
                    leftTopPoint.current,
                );
                const offset = subPoint(newCenterOffset, data.centerOffset);
                data.centerOffset.x += newCenterOffset.x;
                data.centerOffset.y += newCenterOffset.y;
                data.anchor.points![0] = subPoint(data.anchor.points![0], offset);
                data.anchor.points![1] = subPoint(data.anchor.points![1], offset);
                centerPointOffset = offset;
            }

            computedSegmentBaseInfo(newLayer, oldInfo.current, centerPointOffset);

            framework.stage.addOrUpdate(newLayer);
            framework.operate.cursorTip.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width: newLayer.width,
                height: newLayer.height,
                isMin: false,
                rotate: newLayer.rotate,
            });

            if (e.isEnd) {
                framework.operate.cursorTip.clear();
                framework.history.write(oldInfo.current, trackConfig[LayerType.MARKUP_LAYER].rotate);
                framework.operate.setIsBaseOperate(false);
                framework.operate.setActiveLayer(layer.id);
                updateScreenshotUrl(identity, layer.id);
            }
        },
    };
};
